import { useState, useEffect } from "react";
import { UserCog } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiErrorDetail } from "@/lib/api";
import { DOSSIER_EDIT } from "@/constants/testIds";
import { toast } from "sonner";

const FIELDS = [
  { key: "phone", label: "Teléfono" },
  { key: "curp", label: "CURP" },
  { key: "rfc", label: "RFC" },
  { key: "nss", label: "NSS (Seguro Social)" },
  { key: "fecha_nacimiento", label: "Fecha de nacimiento", type: "date" },
  { key: "nacionalidad", label: "Nacionalidad" },
  { key: "domicilio", label: "Domicilio" },
  { key: "beneficiarios", label: "Beneficiarios" },
  { key: "contacto_emergencia_nombre", label: "Contacto de emergencia - Nombre" },
  { key: "contacto_emergencia_telefono", label: "Contacto de emergencia - Teléfono" },
  { key: "contacto_emergencia_parentesco", label: "Contacto de emergencia - Parentesco" },
];

export function DossierEditDialog({ employeeId, employee, onUpdated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [workCenters, setWorkCenters] = useState([]);

  useEffect(() => { api.get("/work-centers").then(({ data }) => setWorkCenters(data)); }, []);

  const handleOpen = (v) => {
    setOpen(v);
    if (v) {
      setForm({
        ...Object.fromEntries(FIELDS.map((f) => [f.key, employee[f.key] || (f.key === "nacionalidad" ? "Mexicana" : "")])),
        work_center: employee.work_center || "",
      });
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/employees/${employeeId}/dossier`, form);
      toast.success(data.message);
      setOpen(false);
      onUpdated();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpen}>
      <DialogTrigger asChild>
        <Button data-testid={DOSSIER_EDIT.openButton} variant="outline" className="rounded-full border-petroleum/30 text-petroleum hover:bg-petroleum/5 shrink-0">
          <UserCog className="w-4 h-4 mr-2" /> Editar expediente
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-bone max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Datos personales del expediente</DialogTitle></DialogHeader>
        {form && (
          <div className="space-y-3">
            <div>
              <Label>Centro de trabajo</Label>
              <select data-testid={`${DOSSIER_EDIT.fieldInputPrefix}-work_center`} value={form.work_center} onChange={(e) => setForm((prev) => ({ ...prev, work_center: e.target.value }))} className="w-full border border-petroleum/20 rounded-lg px-3 py-2 bg-white text-sm">
                <option value="">Sin centro asignado</option>
                {workCenters.map((wc) => <option key={wc.id} value={wc.name}>{wc.name}</option>)}
              </select>
            </div>
            {FIELDS.map((f) => (
              <div key={f.key}>
                <Label>{f.label}</Label>
                <Input
                  data-testid={`${DOSSIER_EDIT.fieldInputPrefix}-${f.key}`}
                  type={f.type || "text"}
                  value={form[f.key]}
                  onChange={(e) => setForm((prev) => ({ ...prev, [f.key]: e.target.value }))}
                />
              </div>
            ))}
            <Button data-testid={DOSSIER_EDIT.saveButton} disabled={saving} onClick={handleSave} className="w-full bg-petroleum rounded-full">
              {saving ? "Guardando..." : "Guardar"}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
